import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signOut, signInWithPopup, sendEmailVerification, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, actionCodeSettings, db, functions } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const logout = () => {
    return signOut(auth);
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  const signUpWithEmail = async (email, password, userProfile = {}, sendVerification = true) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      if (result.user) {
        // Create user profile in Firestore
        const userData = {
          email: result.user.email,
          uid: result.user.uid,
          firstName: userProfile.firstName || '',
          lastName: userProfile.lastName || '',
          phoneNumber: userProfile.phoneNumber || '',
          userType: 'learner',
          isProfileComplete: false,
          emailVerified: false,
          emailVerificationSentAt: sendVerification ? serverTimestamp() : null,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        };

        await setDoc(doc(db, 'users', result.user.uid), userData);
        console.log('User profile created in Firestore');

        if (sendVerification) {
          console.log('Sending verification email to:', result.user.email);
          await sendEmailVerification(result.user, actionCodeSettings);
          console.log('Verification email sent successfully');
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  const sendVerificationEmail = async (user = currentUser) => {
    try {
      if (user) {
        await sendEmailVerification(user, actionCodeSettings);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error sending verification email:', error);
      throw error;
    }
  };

  // Custom verification functions that use Firebase Functions with Resend
  const sendCustomVerificationEmail = async (isResend = false) => {
    try {
      if (!currentUser) {
        throw new Error('No user is currently logged in');
      }

      // Try to use custom Resend function first
      try {
        const sendCustomVerificationEmailFunction = httpsCallable(functions, 'sendCustomVerificationEmail');
        
        // Get current domain for the verification link
        const currentDomain = window.location.origin;
        const continueUrl = `${currentDomain}/verify-email`;
        
        const result = await sendCustomVerificationEmailFunction({ 
          isResend, 
          continueUrl 
        });
        return result.data;
      } catch (functionError) {
        console.log('Custom function not available, falling back to Firebase Auth email:', functionError);
        
        // Fallback to Firebase Auth verification email
        const dynamicActionCodeSettings = {
          url: `${window.location.origin}/verify-email`,
          handleCodeInApp: true,
        };
        await sendEmailVerification(currentUser, dynamicActionCodeSettings);
        
        // Update Firestore to track when verification email was sent
        await setDoc(doc(db, 'users', currentUser.uid), {
          emailVerificationSentAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        return { success: true, message: 'Verification email sent successfully' };
      }
    } catch (error) {
      console.error('Error sending custom verification email:', error);
      throw error;
    }
  };

  const checkEmailVerification = async () => {
    try {
      if (!currentUser) {
        throw new Error('No user is currently logged in');
      }

      // Reload user to get latest verification status
      await currentUser.reload();
      
      // Get user document from Firestore
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      const firebaseVerified = currentUser.emailVerified;
      const customVerified = userData.emailVerified || false;
      
      // IMPORTANT: Only use our custom verification status, never auto-update from Firebase Auth
      // The user must explicitly verify through our verification flow
      const isVerified = customVerified;
      
      return {
        isVerified,
        firebaseVerified,
        customVerified,
        email: currentUser.email,
        verifiedAt: userData.emailVerifiedAt
      };
    } catch (error) {
      console.error('Error checking email verification:', error);
      throw error;
    }
  };

  const verifyUserEmail = async () => {
    try {
      if (!currentUser) {
        throw new Error('No user is currently logged in');
      }

      // Update Firestore to mark email as verified
      await setDoc(doc(db, 'users', currentUser.uid), {
        emailVerified: true,
        emailVerifiedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      return { success: true, message: 'Email verified successfully' };
    } catch (error) {
      console.error('Error verifying user email:', error);
      throw error;
    }
  };

  const value = {
    currentUser,
    logout,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    sendVerificationEmail,
    sendCustomVerificationEmail,
    checkEmailVerification,
    verifyUserEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};